'use client';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { INotificationCenterSubscription } from '@/api/notificationCenter';
import { fetcherSS } from '@/modules/api';
import { useNotificationCenterSubscription } from '@/modules/common/hooks/useNotificationCenterSubscription';
import { normalizeSubscriptionUrl } from '../helpers/normalizeSubscriptionUrl';
import { RealTimeService } from '../helpers/RealTimeService';

export enum CONNECTION_STATUS {
  DISCONNECTED = 'DISCONNECTED',
  CONNECTED = 'CONNECTED',
}

const RealTimeContext = createContext<{ status: CONNECTION_STATUS }>({
  status: CONNECTION_STATUS.DISCONNECTED,
});

export const useRealTimeStatus = () => useContext(RealTimeContext);

export const RealTimeProvider = ({ children }: PropsWithChildren) => {
  const [status, setStatus] = useState(CONNECTION_STATUS.DISCONNECTED);

  const { data: subscription } = useNotificationCenterSubscription();
  const subscriptionUrl = subscription?.url;
  const subscriptionToken = subscription?.token;

  const getToken = useCallback(async () => {
    const data = await fetcherSS<INotificationCenterSubscription>(
      '/notification_center/centrifugo/subscription'
    );
    if (!data?.token) throw new Error('Failed to refresh centrifugo token');
    return data.token;
  }, []);

  useEffect(() => {
    const resolvedUrl = normalizeSubscriptionUrl(subscriptionUrl);

    if (!(resolvedUrl && subscriptionToken)) return;

    const realTimeService = RealTimeService.getInstance();
    realTimeService.connect(resolvedUrl, subscriptionToken, {
      getToken,
      onConnected: () => setStatus(CONNECTION_STATUS.CONNECTED),
      onDisconnected: () => setStatus(CONNECTION_STATUS.DISCONNECTED),
    });

    return () => {
      realTimeService.disconnect();
      setStatus(CONNECTION_STATUS.DISCONNECTED);
    };
  }, [subscriptionUrl, subscriptionToken, getToken]);

  return (
    <RealTimeContext.Provider value={{ status }}>
      {children}
    </RealTimeContext.Provider>
  );
};
